import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, RefreshCw, ChevronLeft, Loader2, Award, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { AdminStatsCards } from '../components/admin/AdminStatsCards';
import { PendingUsersTab } from '../components/admin/PendingUsersTab';
import { AllUsersTab } from '../components/admin/AllUsersTab';
import { ActivationCodesTab } from '../components/admin/ActivationCodesTab';
import { AnalyticsTab } from '../components/admin/AnalyticsTab';
import { AdminSettingsTab } from '../components/admin/AdminSettingsTab';
import { BadgesTab } from '../components/admin/BadgesTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = 't.m@mimc.online';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [activationCodes, setActivationCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [newCodeForm, setNewCodeForm] = useState({ code: '', uses: 1 });
  const [bulkGenForm, setBulkGenForm] = useState({ prefix: 'EGNS2026', startNumber: 1001, count: 100, usesAllowed: 1 });
  const [generatingCodes, setGeneratingCodes] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [contactSettings, setContactSettings] = useState({
    whatsapp_number: '', whatsapp_display: '', whatsapp_message: '', phone_number: '', phone_display: ''
  });
  const [badgeOverview, setBadgeOverview] = useState(null);

  const isAdmin = user?.email === ADMIN_EMAIL || user?.is_admin === true;

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allUsersRes, codesRes, analyticsRes, contactRes, badgesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/pending-users`),
        axios.get(`${API}/admin/all-users`),
        axios.get(`${API}/admin/activation-codes`),
        axios.get(`${API}/admin/analytics`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings/contact`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/badges/overview`).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data.users || []);
      setAllUsers(allUsersRes.data.users || []);
      setActivationCodes(codesRes.data.codes || []);
      setAnalytics(analyticsRes.data);
      if (contactRes.data) setContactSettings(prev => ({ ...prev, ...contactRes.data }));
      setBadgeOverview(badgesRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, userName) => {
    setActionLoading(prev => ({ ...prev, [userId]: 'approve' }));
    try {
      await axios.post(`${API}/admin/users/${userId}/approve`);
      toast.success(`${userName} approved!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleRejectUser = async (userId, userName) => {
    const reason = window.prompt('Enter rejection reason (optional):');
    setActionLoading(prev => ({ ...prev, [userId]: 'reject' }));
    try {
      await axios.post(`${API}/admin/users/${userId}/reject`, null, { params: { reason } });
      toast.success(`${userName} rejected`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    if (!newCodeForm.code.trim()) { toast.error('Please enter a code'); return; }
    try {
      await axios.post(`${API}/admin/activation-codes`, {
        code: newCodeForm.code.trim(),
        uses_allowed: parseInt(newCodeForm.uses) || 1
      });
      toast.success(`Code "${newCodeForm.code}" created!`);
      setNewCodeForm({ code: '', uses: 1 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Delete code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/activation-codes/${code}`);
      toast.success(`Code "${code}" deleted`);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  const handleGenerateBulkCodes = async (e) => {
    e.preventDefault();
    setGeneratingCodes(true);
    try {
      const response = await axios.post(`${API}/admin/activation-codes/generate-bulk`, {
        prefix: bulkGenForm.prefix,
        start_number: parseInt(bulkGenForm.startNumber),
        count: parseInt(bulkGenForm.count),
        uses_allowed: parseInt(bulkGenForm.usesAllowed)
      });
      setLastGenerated(response.data);
      toast.success(`Generated ${response.data.created_count} codes!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate codes');
    } finally {
      setGeneratingCodes(false);
    }
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b bg-card/50 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-teal-500" />
              <h1 className="font-heading text-xl font-bold">Admin Dashboard</h1>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-data">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <AdminStatsCards stats={stats} />

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-5">
            <TabsTrigger value="pending" className="relative">
              Pending
              {pendingUsers.length > 0 && <Badge className="ml-2 bg-yellow-500 text-white">{pendingUsers.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="users">All Users</TabsTrigger>
            <TabsTrigger value="codes">Codes</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab">Analytics</TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab"><Settings className="h-4 w-4 mr-1" />Settings</TabsTrigger>
            <TabsTrigger value="badges" data-testid="badges-tab"><Award className="h-4 w-4 mr-1" />Badges</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <PendingUsersTab pendingUsers={pendingUsers} actionLoading={actionLoading} onApprove={handleApproveUser} onReject={handleRejectUser} />
          </TabsContent>

          <TabsContent value="users">
            <AllUsersTab allUsers={allUsers} searchTerm={searchTerm} onSearchChange={setSearchTerm} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="codes">
            <ActivationCodesTab
              activationCodes={activationCodes} newCodeForm={newCodeForm} setNewCodeForm={setNewCodeForm}
              onCreateCode={handleCreateCode} bulkGenForm={bulkGenForm} setBulkGenForm={setBulkGenForm}
              generatingCodes={generatingCodes} onGenerateBulk={handleGenerateBulkCodes}
              lastGenerated={lastGenerated} onDeleteCode={handleDeleteCode}
            />
          </TabsContent>

          <TabsContent value="analytics" data-testid="analytics-content">
            <AnalyticsTab analytics={analytics} />
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-content">
            <AdminSettingsTab contactSettings={contactSettings} setContactSettings={setContactSettings} />
          </TabsContent>

          <TabsContent value="badges" data-testid="badges-content">
            <BadgesTab badgeOverview={badgeOverview} setBadgeOverview={setBadgeOverview} allUsers={allUsers} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
